// Lightweight analytics tracker for Take The Stage.
// - Persistent session_id (localStorage) survives across visits.
// - Per-load visit_id (sessionStorage) marks a single browsing session.
// - Sends: pageview once per load + scroll-depth milestones + cta clicks + form submits.

import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getSessionId() {
  try {
    let id = localStorage.getItem("tts_session_id");
    if (!id) {
      id = uuid();
      localStorage.setItem("tts_session_id", id);
    }
    return id;
  } catch {
    return uuid();
  }
}

function getVisitId() {
  try {
    let id = sessionStorage.getItem("tts_visit_id");
    if (!id) {
      id = uuid();
      sessionStorage.setItem("tts_visit_id", id);
    }
    return id;
  } catch {
    return uuid();
  }
}

function utmFromLocation() {
  try {
    const p = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
    const utm = {};
    keys.forEach((k) => {
      const v = p.get(k);
      if (v) utm[k] = v;
    });
    return utm;
  } catch {
    return {};
  }
}

async function send(payload) {
  try {
    // Use keepalive-friendly fetch if available, fall back to axios.
    if (navigator && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      const ok = navigator.sendBeacon(`${API}/analytics/track`, blob);
      if (ok) return;
    }
    await axios.post(`${API}/analytics/track`, payload, { timeout: 6000 });
  } catch (e) {
    // Silently ignore analytics failures
  }
}

function commonPayload() {
  return {
    session_id: getSessionId(),
    visit_id: getVisitId(),
    path: window.location.pathname + window.location.search,
    referrer: document.referrer || "",
    screen: `${window.innerWidth}x${window.innerHeight}`,
    utm: utmFromLocation(),
  };
}

let _pageviewSent = false;
export function trackPageview() {
  if (_pageviewSent) return;
  _pageviewSent = true;
  send({ ...commonPayload(), type: "pageview" });
}

export function trackEvent(name, meta = {}) {
  send({ ...commonPayload(), type: "event", event_name: name, event_meta: meta });
}

let _scrollBucketsSent = new Set();
function onScroll() {
  try {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop || 0;
    const winH = window.innerHeight || doc.clientHeight;
    const docH = doc.scrollHeight || document.body.scrollHeight;
    if (docH <= winH + 4) return;
    const pct = Math.min(100, Math.round(((scrollTop + winH) / docH) * 100));
    for (const bkt of [25, 50, 75, 100]) {
      if (pct >= bkt && !_scrollBucketsSent.has(bkt)) {
        _scrollBucketsSent.add(bkt);
        trackEvent("scroll_depth", { pct: bkt });
      }
    }
  } catch {
    // no-op
  }
}

let _scrollBound = false;
export function initTracking() {
  trackPageview();
  if (_scrollBound) return;
  _scrollBound = true;
  let ticking = false;
  window.addEventListener(
    "scroll",
    () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          onScroll();
          ticking = false;
        });
        ticking = true;
      }
    },
    { passive: true }
  );
  // Also fire on load in case someone lands mid-page (deep link)
  setTimeout(onScroll, 400);
}
